<template>
  <div class="product-search" @focusout="onFocusOut">
    <form role="search" @submit.prevent="submit">
      <search-icon class="search-icon" aria-hidden="true" />
      <input ref="input" :value="value" type="search" maxlength="100" placeholder="Пошук"
        aria-label="Пошук товарів" role="combobox" aria-autocomplete="list"
        :aria-expanded="isOpen ? 'true' : 'false'" :aria-controls="listId"
        :aria-activedescendant="activeIndex >= 0 && isOpen ? optionId(activeIndex) : null"
        autocomplete="off" @input="onInput" @focus="open" @keydown="onKeydown"
        @compositionstart="composing = true" @compositionend="onCompositionEnd" />
      <button class="submit-search" type="submit" aria-label="Шукати" :disabled="!query.length">→</button>
    </form>
    <div v-if="isOpen" class="suggestions">
      <p class="search-status" role="status">{{ statusText }}</p>
      <ul :id="listId" role="listbox" aria-label="Пошукові підказки">
        <li v-for="(option, index) in options" :id="optionId(index)" :key="option.key"
          role="option" :aria-selected="activeIndex === index ? 'true' : 'false'"
          @mousedown.prevent @click="choose(option)">
          <span class="option-type">{{ option.type === 'product' ? 'Товар' : option.type === 'category' ? 'Категорія' : 'Нещодавній запит' }}</span>
          <span>{{ option.name }}</span>
          <span v-if="option.type === 'product' && option.value.code" class="option-type">Артикул: {{ option.value.code }}</span>
        </li>
      </ul>
      <button v-if="error" type="button" class="search-action" @click="loadSuggestions">Спробувати ще раз</button>
      <button v-if="query.length" type="button" class="search-action" @click="search(query)">{{ query.length >= 3 ? 'Усі результати' : 'Шукати точний артикул' }} для «{{ query }}»</button>
      <button v-if="recent.length" type="button" class="search-action clear-history" @click="clearHistory">Очистити історію пошуку</button>
    </div>
  </div>
</template>

<script>
import { SearchIcon } from 'vue-feather-icons';
import { GET_SHOP_PRODUCTS_SEARCH } from '../resources/js/helpers/requests';

export default {
  name: 'ProductSearch',
  components: { SearchIcon },
  props: {
    value: { type: String, default: '' },
    categories: { type: Array, default: () => [] },
  },
  data() {
    return { isOpen: false, activeIndex: -1, products: [], recent: [], loading: false, error: false,
      timer: null, requestId: 0, composing: false, historyKey: null };
  },
  computed: {
    query() { return this.value.trim(); },
    listId() { return `search-suggestions-${this._uid}`; },
    options() {
      const query = this.query.toLocaleLowerCase('uk');
      const options = this.recent.filter(name => !query || name.toLocaleLowerCase('uk').includes(query))
        .slice(0, 5).map(name => ({ type: 'recent', key: 'recent-' + name, name }));
      if (query.length < 3) return options;
      const seen = new Set();
      const visit = categories => categories.forEach(category => {
        if (!seen.has(String(category.id))) {
          seen.add(String(category.id));
          if (category.name.toLocaleLowerCase('uk').includes(query)) options.push({ type: 'category', key: 'category-' + category.id, name: category.name, value: category });
          visit(category.children || []);
        }
      });
      visit(this.categories);
      return options.filter(option => option.type !== 'category').concat(options.filter(option => option.type === 'category').slice(0, 3),
        this.products.map(product => ({ type: 'product', key: 'product-' + product.id, name: product.name, value: product })));
    },
    statusText() {
      if (this.query.length < 3) return 'Введіть від 3 символів назви або підтвердьте точний артикул кнопкою пошуку.';
      if (this.loading) return 'Шукаємо товари…';
      if (this.error) return 'Не вдалося завантажити товари. Можна повторити пошук.';
      return this.options.length ? 'Оберіть підказку або перегляньте всі результати.' : 'Підказок немає. Спробуйте інший запит.';
    },
  },
  mounted() {
    const account = window.Telegram?.WebApp?.initDataUnsafe?.user?.id || window.location.pathname.split('/').filter(Boolean)[0];
    this.historyKey = /^\d+$/.test(String(account || '')) ? `medhouse:recent-searches:${account}` : null;
    try {
      const saved = JSON.parse((this.historyKey && window.localStorage.getItem(this.historyKey)) || '[]');
      this.recent = Array.isArray(saved) ? saved.filter(item => typeof item === 'string' && item.trim().length >= 1 && item.length <= 100).slice(0, 5) : [];
    } catch (_) { this.recent = []; }
  },
  beforeDestroy() { this.cancel(); },
  watch: {
    value() {
      this.cancel(); this.products = []; this.error = false; this.activeIndex = -1;
      if (this.isOpen && !this.composing) this.schedule();
    },
  },
  methods: {
    optionId(index) { return `${this.listId}-${index}`; },
    focus() { this.$refs.input.focus(); },
    select() { this.$refs.input.select(); },
    cancel() { clearTimeout(this.timer); this.requestId++; this.loading = false; },
    close() { this.isOpen = false; this.activeIndex = -1; this.cancel(); },
    open() { this.isOpen = true; this.activeIndex = -1; this.schedule(); },
    schedule() {
      clearTimeout(this.timer);
      if (this.query.length >= 3) {
        this.loading = true;
        this.timer = setTimeout(() => this.loadSuggestions(), 300);
      }
    },
    onInput(event) { this.isOpen = true; this.$emit('input', event.target.value); },
    onCompositionEnd(event) { this.composing = false; this.onInput(event); this.$nextTick(() => this.schedule()); },
    onFocusOut(event) { if (!event.relatedTarget || !this.$el.contains(event.relatedTarget)) this.close(); },
    async loadSuggestions() {
      clearTimeout(this.timer);
      if (!this.isOpen || this.query.length < 3) return;
      const id = ++this.requestId;
      this.loading = true; this.error = false; this.products = []; this.activeIndex = -1;
      try {
        const result = await GET_SHOP_PRODUCTS_SEARCH({ q: this.query });
        if (!Array.isArray(result.data)) throw new Error('Invalid search response');
        if (id === this.requestId) this.products = result.data.slice(0, 5);
      } catch (_) {
        if (id === this.requestId) this.error = true;
      } finally {
        if (id === this.requestId) this.loading = false;
      }
    },
    saveHistory(query) {
      this.recent = [query, ...this.recent.filter(item => item.toLocaleLowerCase('uk') !== query.toLocaleLowerCase('uk'))].slice(0, 5);
      try { if (this.historyKey) window.localStorage.setItem(this.historyKey, JSON.stringify(this.recent)); } catch (_) { /* Search also works when storage is unavailable. */ }
    },
    clearHistory() {
      this.recent = []; this.activeIndex = -1;
      try { if (this.historyKey) window.localStorage.removeItem(this.historyKey); } catch (_) { /* Storage may be disabled. */ }
      this.focus();
    },
    search(query) {
      if (!query.length || query.length > 100) return;
      this.saveHistory(query); this.close(); this.$emit('input', query); this.$emit('search', query);
    },
    choose(option) {
      if (option.type === 'recent') return this.search(option.name);
      this.saveHistory(this.query); this.close(); this.$emit(option.type, option.value);
    },
    submit() {
      if (this.composing) return;
      if (this.isOpen && this.options[this.activeIndex]) this.choose(this.options[this.activeIndex]);
      else this.search(this.query);
    },
    onKeydown(event) {
      if (event.isComposing || this.composing) return;
      if (event.key === 'Escape') { event.preventDefault(); this.close(); }
      if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
        event.preventDefault();
        if (!this.isOpen) this.open();
        const count = this.options.length;
        if (count) {
          this.activeIndex = event.key === 'ArrowDown' ? (this.activeIndex + 1) % count : (this.activeIndex <= 0 ? count - 1 : this.activeIndex - 1);
          this.$nextTick(() => document.getElementById(this.optionId(this.activeIndex))?.scrollIntoView({ block: 'nearest' }));
        }
      }
    },
  },
};
</script>

<style scoped>
.product-search { position: relative; flex: 1; min-width: 0; }
form { display: flex; align-items: center; position: relative; }
input { width: 100%; min-width: 0; height: 48px; padding: 12px 48px 12px 44px; border: 1px solid transparent; border-radius: 8px; background: #f0f0f0; color: #333; font: 16px/24px Inter, sans-serif; }
.search-icon { position: absolute; left: 12px; width: 20px; color: #707070; pointer-events: none; }
button { min-height: 44px; cursor: pointer; font: inherit; }
.submit-search { position: absolute; right: 2px; width: 44px; border: 0; background: transparent; color: #075a63; font-size: 24px; }
button:disabled { opacity: .5; cursor: default; }
input:focus-visible, button:focus-visible { outline: 2px solid #075a63; outline-offset: 2px; }
.suggestions { position: absolute; top: calc(100% + 8px); left: 0; right: 0; z-index: 20; max-height: min(60vh, 440px); overflow-y: auto; border: 1px solid #d2d6db; border-radius: 8px; background: #fff; box-shadow: 0 8px 24px #0000001a; }
.search-status { margin: 0; padding: 12px; color: #555; font: 13px/18px Inter, sans-serif; }
ul { list-style: none; padding: 0; margin: 0; }
li { min-height: 44px; padding: 10px 12px; cursor: pointer; color: #333; font: 14px/20px Inter, sans-serif; overflow-wrap: anywhere; border-top: 1px solid #eee; }
li:hover, li[aria-selected="true"] { background: #e6f7f8; }
.option-type { display: block; font-size: 12px; color: #52656a; }
.search-action { display: block; width: 100%; padding: 10px 12px; text-align: left; border: 0; border-top: 1px solid #eee; background: #fff; color: #075a63; overflow-wrap: anywhere; }
.clear-history { color: #555; font-size: 13px; }
@media (min-width: 1024px) { input { height: 56px; font-size: 20px; } }
</style>
